import {Button, SkeletonBox, type ButtonProps} from '@primer/react'
import {CopilotIcon, FileCheckIcon, TriangleDownIcon} from '@primer/octicons-react'
import {IconButtonSelector} from '@github-ui/copilot-chat/components/IconButtonSelector'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {SubAgent} from '../../types/agent'
import type {AgentInfo} from '../../types/session'
import styles from './AgentSelector.module.css'
import {clsx} from 'clsx'

type AgentSelectorMenuTriggerProps = {
  isLoading: boolean
  isCopilotSelected: boolean
  selectedAgentObj: SubAgent | AgentInfo | undefined
  agentType: string | undefined
  ariaLabel: string
  disabled: boolean
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  className?: string
  /** When true, the icon trigger renders a trailing dropdown caret (icon + ▾). */
  showDropdownCaret?: boolean
}

// Icon trigger with a trailing dropdown caret (icon + ▾). The icon is the visible
// child; the accessible name comes from `aria-label`, so the caret stays decorative.
function CaretIconTrigger({
  iconNode,
  ariaLabel,
  disabled,
  variant,
  size,
  className,
  ...rest
}: {
  iconNode: React.ReactNode
  ariaLabel: string
  disabled: boolean
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  className?: string
} & React.ComponentPropsWithoutRef<'button'>) {
  return (
    <Button
      type="button"
      trailingAction={TriangleDownIcon}
      aria-label={ariaLabel}
      disabled={disabled}
      variant={variant}
      size={size}
      className={clsx(styles.caretAnchor, className)}
      {...rest}
    >
      <span className={styles.iconBox}>{iconNode}</span>
    </Button>
  )
}

export function renderAgentSelectorMenuTrigger({
  isLoading,
  isCopilotSelected,
  selectedAgentObj,
  agentType,
  ariaLabel,
  disabled,
  variant,
  size,
  className,
  showDropdownCaret = false,
}: AgentSelectorMenuTriggerProps) {
  if (isLoading) {
    const skeletonSize = size === 'small' ? '16px' : '32px'
    return (
      <Button
        aria-label="Loading agents"
        disabled
        variant={variant}
        size={size}
        className={clsx(styles.avatarButtonSkeleton, className)}
      >
        <SkeletonBox width={skeletonSize} height={skeletonSize} data-testid="agent-selector-skeleton" />
      </Button>
    )
  }
  // Special case for Copilot to use icon instead of avatar - This allows Copilot to change with dark mode
  if (isCopilotSelected) {
    return showDropdownCaret ? (
      <CaretIconTrigger
        iconNode={<CopilotIcon size={16} />}
        ariaLabel={ariaLabel}
        disabled={disabled}
        variant={variant}
        size={size}
        className={className}
      />
    ) : (
      <IconButtonSelector
        icon={CopilotIcon}
        selectedIcon={CopilotIcon}
        aria-label={ariaLabel}
        selected={false}
        disabled={disabled}
        variant={variant}
        size={size}
        className={className}
      />
    )
  }
  // custom agent with file check icon
  if (selectedAgentObj && typeof selectedAgentObj === 'object' && agentType === 'customSubAgent') {
    return showDropdownCaret ? (
      <CaretIconTrigger
        iconNode={<FileCheckIcon size={16} />}
        ariaLabel={ariaLabel}
        disabled={disabled}
        variant={variant}
        size={size}
        className={className}
      />
    ) : (
      <IconButtonSelector
        icon={CopilotIcon}
        selectedIcon={FileCheckIcon}
        aria-label={ariaLabel}
        selected={Boolean(selectedAgentObj)}
        disabled={disabled}
        variant={variant}
        size={size}
        className={className}
      />
    )
  }
  // 3rd-party agent with avatar
  if (selectedAgentObj && typeof selectedAgentObj === 'object' && 'avatar_url' in selectedAgentObj) {
    return showDropdownCaret ? (
      <CaretIconTrigger
        iconNode={<GitHubAvatar src={selectedAgentObj.avatar_url} size={16} square />}
        ariaLabel={ariaLabel}
        disabled={disabled || isLoading}
        variant={variant}
        size={size}
        className={className}
      />
    ) : (
      <Button
        aria-label={ariaLabel}
        disabled={disabled || isLoading}
        variant={variant}
        size={size}
        className={clsx(styles.avatarButton, className)}
      >
        <GitHubAvatar src={selectedAgentObj.avatar_url} size={18} square />
      </Button>
    )
  }
  // Fallback for any other case (e.g., topLevel agent without avatar_url - this should never happen)
  return showDropdownCaret ? (
    <CaretIconTrigger
      iconNode={selectedAgentObj ? <FileCheckIcon size={16} /> : <CopilotIcon size={16} />}
      ariaLabel={ariaLabel}
      disabled={disabled}
      variant={variant}
      size={size}
      className={className}
    />
  ) : (
    <IconButtonSelector
      icon={CopilotIcon}
      selectedIcon={FileCheckIcon}
      aria-label={ariaLabel}
      selected={Boolean(selectedAgentObj)}
      disabled={disabled}
      variant={variant}
      size={size}
      className={className}
    />
  )
}
